import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DropdownSelect from '../Generic/DropdownSelect';
import GlassWorkspaceShell from '../Glass/GlassWorkspaceShell';
import { STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS } from '../Product/stockItemDatePicker';
import { getSupplierSelectList } from '../../clients/supplier_client';
import { getProjects } from '../../clients/project_client';
import { getAllBudgetLines } from '../../clients/budget_line_client';
import { AppContext } from '../../App';
import { parseLabDataDateRangeFromSearch, formatBudgetLineLabel } from '../../utils/labData';
import './LabDataStyle.css';

export const LabDataFilterContext = createContext(null);

/**
 * Shared Lab Data chrome: neutral glass shell, sub-nav, and URL-backed filters.
 * Spend overview also shows budget, supplier, and project pickers.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} props.title
 * @param {boolean} [props.showSpendFilters=false]
 */
const LabDataLayout = ({ children, title, showSpendFilters = false }) => {
  const { token } = useContext(AppContext);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { from, to } = parseLabDataDateRangeFromSearch(searchParams);
  const budgetLineId =
    searchParams.get('budget_id') ||
    searchParams.get('budget_line_id') ||
    '';
  const projectId = searchParams.get('project_id') || '';
  const supplierId = searchParams.get('supplier_id') || '';

  const filterOptions = useMemo(
    () => ({
      from,
      to,
      budgetLineId: budgetLineId || null,
      projectId: projectId || null,
      supplierId: supplierId || null,
    }),
    [from, to, budgetLineId, projectId, supplierId]
  );

  const updateParam = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value == null || value === '') {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setDateRange = useCallback(
    (nextFrom, nextTo) => {
      const next = new URLSearchParams(searchParams);
      if (nextFrom) next.set('from', nextFrom);
      if (nextTo) next.set('to', nextTo);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const fromDate = from ? new Date(`${from}T00:00:00`) : null;
  const toDate = to ? new Date(`${to}T00:00:00`) : null;

  const isSpend = location.pathname === '/lab-data';
  const isBudgets =
    location.pathname === '/lab-data/budgets' ||
    location.pathname.startsWith('/lab-data/budgets/');

  const preservedQuery = searchParams.toString();

  return (
    <LabDataFilterContext.Provider value={filterOptions}>
      <GlassWorkspaceShell theme="neutral" wide testId="lab-data-shell">
        <div className="glass-page-heading">
          <h2>{title}</h2>
        </div>

        <nav className="lab-data-subnav" aria-label="Lab data sections">
          <Link
            to={`/lab-data${preservedQuery ? `?${preservedQuery}` : ''}`}
            className={isSpend ? 'active' : ''}
          >
            Spend overview
          </Link>
          <Link
            to={`/lab-data/budgets${preservedQuery ? `?${preservedQuery}` : ''}`}
            className={isBudgets ? 'active' : ''}
          >
            Budget burn
          </Link>
        </nav>

        <div className="glass-toolbar lab-data-filters" data-testid="lab-data-toolbar">
          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md={3}>
              <span className="lab-data-filter-label">From</span>
              <DatePicker
                selected={fromDate}
                onChange={date => {
                  if (!date) return;
                  const iso = date.toISOString().split('T')[0];
                  setDateRange(iso, to);
                }}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                {...STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <span className="lab-data-filter-label">To</span>
              <DatePicker
                selected={toDate}
                onChange={date => {
                  if (!date) return;
                  const iso = date.toISOString().split('T')[0];
                  setDateRange(from, iso);
                }}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                {...STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS}
              />
            </Col>

            {showSpendFilters && token && (
              <>
                <Col xs={12} md={3}>
                  <BudgetLineFilter
                    token={token}
                    value={budgetLineId}
                    onChange={v => {
                      const next = new URLSearchParams(searchParams);
                      next.delete('budget_line_id');
                      if (v == null || v === '') {
                        next.delete('budget_id');
                      } else {
                        next.set('budget_id', String(v));
                      }
                      setSearchParams(next);
                    }}
                  />
                </Col>
                <Col xs={12} md={3}>
                  <SupplierFilter
                    token={token}
                    value={supplierId}
                    onChange={v => updateParam('supplier_id', v)}
                  />
                </Col>
                <Col xs={12} md={3}>
                  <ProjectFilter
                    token={token}
                    value={projectId}
                    onChange={v => updateParam('project_id', v)}
                  />
                </Col>
              </>
            )}
          </Row>
        </div>

        {children}
      </GlassWorkspaceShell>
    </LabDataFilterContext.Provider>
  );
};

/**
 * Active-budget picker. Writes `budget_id` and drops the legacy query key.
 */
function BudgetLineFilter({ token, value, onChange }) {
  const [options, setOptions] = React.useState([]);
  React.useEffect(() => {
    getAllBudgetLines(token, { isActive: true }).then(res => {
      if (res.success) {
        setOptions(
          res.data.map(line => ({
            value: String(line.id),
            label: formatBudgetLineLabel(line),
          }))
        );
      }
    });
  }, [token]);

  return (
    <DropdownSelect
      label="Budget"
      optionsList={[{ value: '', label: 'All budgets' }, ...options]}
      selectedValue={value ? { value, label: '' } : { value: '', label: 'All budgets' }}
      setSelectedValue={opt => onChange(opt?.value || '')}
      clearable
    />
  );
}

/**
 * Supplier picker for the spend overview filters.
 */
function SupplierFilter({ token, value, onChange }) {
  const [options, setOptions] = React.useState([]);
  React.useEffect(() => {
    getSupplierSelectList(token, data => {
      setOptions(data || []);
    });
  }, [token]);

  return (
    <DropdownSelect
      label="Supplier"
      optionsList={[{ value: '', label: 'All suppliers' }, ...options]}
      selectedValue={value ? { value, label: '' } : { value: '', label: 'All suppliers' }}
      setSelectedValue={opt => onChange(opt?.value || '')}
      clearable
    />
  );
}

/**
 * Project picker for the spend overview filters.
 */
function ProjectFilter({ token, value, onChange }) {
  const [options, setOptions] = React.useState([]);
  React.useEffect(() => {
    getProjects(token, projects => {
      setOptions(
        (projects || []).map(p => ({
          value: String(p.id),
          label: p.name,
        }))
      );
    });
  }, [token]);

  return (
    <DropdownSelect
      label="Project"
      optionsList={[{ value: '', label: 'All projects' }, ...options]}
      selectedValue={value ? { value, label: '' } : { value: '', label: 'All projects' }}
      setSelectedValue={opt => onChange(opt?.value || '')}
      clearable
    />
  );
}

/**
 * Date range and optional spend filters from the Lab Data layout.
 *
 * @returns {{ from: string, to: string, budgetLineId: string|null, projectId: string|null, supplierId: string|null }|null}
 */
export function useLabDataFilters() {
  return useContext(LabDataFilterContext);
}

export default LabDataLayout;
